import { useState } from 'react';
import { useMutation } from '@apollo/client/react';
import { gql } from '@apollo/client';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import {
  validateFileForUpload,
  getFileValidationErrorMessage,
  getMaxFileSizeDisplay,
  getSupportedFileTypesDisplay,
  getFileInputAcceptAttribute,
} from '../utils/file-validation';

const GET_DOCUMENT_UPLOAD_URL = gql`
  mutation GetDocumentUploadUrl($input: DocumentUploadInput!) {
    getDocumentUploadUrl(input: $input) {
      uploadUrl
      s3Key
    }
  }
`;

const CREATE_DOCUMENT = gql`
  mutation CreateDocument($input: CreateDocumentInput!) {
    createDocument(input: $input) {
      id
      fileName
      fileType
      fileSize
      createdAt
    }
  }
`;

const GET_INQUIRY_ATTACHMENT_UPLOAD_URL = gql`
  mutation GetInquiryAttachmentUploadUrl($input: InquiryAttachmentUploadInput!) {
    getInquiryAttachmentUploadUrl(input: $input) {
      uploadUrl
      s3Key
    }
  }
`;

const ADD_INQUIRY_ATTACHMENT = gql`
  mutation AddInquiryAttachment($input: AddInquiryAttachmentInput!) {
    addInquiryAttachment(input: $input) {
      id
      fileName
      fileType
      fileSize
      uploadedAt
    }
  }
`;

interface UploadUrlResult {
  uploadUrl: string;
  s3Key: string;
}

interface GetDocumentUploadUrlData {
  getDocumentUploadUrl: UploadUrlResult;
}

interface GetInquiryAttachmentUploadUrlData {
  getInquiryAttachmentUploadUrl: UploadUrlResult;
}

interface CreateDocumentData {
  createDocument: {
    id: string;
    fileName: string;
    fileType: string;
    fileSize: number;
    createdAt: string;
  };
}

interface AddInquiryAttachmentData {
  addInquiryAttachment: {
    id: string;
    fileName: string;
    fileType: string;
    fileSize: number;
    uploadedAt: string;
  };
}

interface DocumentUploadProps {
  entityId: string; // caseId or inquiryId
  entityType: 'case' | 'inquiry';
  onUploadComplete?: () => void;
  onActivityLog?: (message: string) => void;
}

export function DocumentUpload({ entityId, entityType, onUploadComplete, onActivityLog }: DocumentUploadProps) {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [description, setDescription] = useState('');
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  const [getDocumentUploadUrl] = useMutation<GetDocumentUploadUrlData>(GET_DOCUMENT_UPLOAD_URL);
  const [createDocument] = useMutation<CreateDocumentData>(CREATE_DOCUMENT);
  const [getInquiryAttachmentUploadUrl] = useMutation<GetInquiryAttachmentUploadUrlData>(GET_INQUIRY_ATTACHMENT_UPLOAD_URL);
  const [addInquiryAttachment] = useMutation<AddInquiryAttachmentData>(ADD_INQUIRY_ATTACHMENT);

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      // Validate file using shared validation
      const validation = validateFileForUpload(file.name, file.type, file.size);
      
      if (!validation.isValid) {
        const errorMessage = getFileValidationErrorMessage(validation);
        alert(errorMessage);
        return;
      }

      setSelectedFile(file);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      alert('Please select a file to upload');
      return;
    }

    setUploading(true);
    setUploadProgress(0);

    try {
      // Step 1: Get pre-signed upload URL
      let uploadUrl: string;
      let s3Key: string;

      if (entityType === 'case') {
        const { data } = await getDocumentUploadUrl({
          variables: {
            input: {
              caseId: entityId,
              fileName: selectedFile.name,
              fileType: selectedFile.type,
              fileSize: selectedFile.size,
            },
          },
        });
        uploadUrl = data!.getDocumentUploadUrl.uploadUrl;
        s3Key = data!.getDocumentUploadUrl.s3Key;
      } else {
        const { data } = await getInquiryAttachmentUploadUrl({
          variables: {
            input: {
              fileName: selectedFile.name,
              fileType: selectedFile.type,
              fileSize: selectedFile.size,
            },
          },
        });
        uploadUrl = data!.getInquiryAttachmentUploadUrl.uploadUrl;
        s3Key = data!.getInquiryAttachmentUploadUrl.s3Key;
      }

      setUploadProgress(30);

      // Step 2: Upload file to S3
      const uploadResponse = await fetch(uploadUrl, {
        method: 'PUT',
        body: selectedFile,
        headers: {
          'Content-Type': selectedFile.type,
        },
      });

      if (!uploadResponse.ok) {
        throw new Error('Failed to upload file to S3');
      }

      setUploadProgress(70);

      // Step 3: Create document record (only for cases, inquiries handle this differently)
      if (entityType === 'case') {
        console.log('Creating document record in DynamoDB...');
        const result = await createDocument({
          variables: {
            input: {
              caseId: entityId,
              fileName: selectedFile.name,
              fileType: selectedFile.type,
              fileSize: selectedFile.size,
              s3Key,
              description: description || undefined,
            },
          },
          refetchQueries: ['ListDocumentsByCase'],
          awaitRefetchQueries: true,
        });
        console.log('Document record created:', result.data?.createDocument);
      } else {
        // For inquiries, add attachment to inquiry record
        console.log('Adding attachment to inquiry in DynamoDB...');
        const result = await addInquiryAttachment({
          variables: {
            input: {
              inquiryId: entityId,
              fileName: selectedFile.name,
              fileType: selectedFile.type,
              fileSize: selectedFile.size,
              s3Key,
              description: description || undefined,
            },
          },
          refetchQueries: ['GetInquiry'],
          awaitRefetchQueries: true,
        });
        console.log('Attachment added to inquiry:', result.data?.addInquiryAttachment);
      }

      setUploadProgress(100);

      // Log activity
      if (onActivityLog) {
        onActivityLog(`Uploaded document: ${selectedFile.name}`);
      }

      // Reset form
      setSelectedFile(null);
      setDescription('');
      setUploadProgress(0);

      // Notify parent component
      if (onUploadComplete) {
        console.log('Calling onUploadComplete callback...');
        await onUploadComplete();
      }

      alert('Document uploaded successfully!');
    } catch (error: any) {
      console.error('Error uploading document:', error);
      alert('Error uploading document: ' + error.message);
    } finally {
      setUploading(false);
    }
  };

  return (
    <Card>
      <CardContent className="p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Upload Document</h3>
        
        <div className="space-y-4">
          {/* File Input */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Select File
            </label>
            <input
              type="file"
              onChange={handleFileSelect}
              accept={getFileInputAcceptAttribute()}
              className="block w-full text-sm text-gray-900 border border-gray-300 rounded-lg cursor-pointer bg-gray-50 focus:outline-none"
              disabled={uploading}
            />
            <p className="text-xs text-gray-500 mt-1">
              Supported formats: {getSupportedFileTypesDisplay()} (Max {getMaxFileSizeDisplay()})
            </p>
          </div>

          {/* Selected File Info */}
          {selectedFile && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
              <p className="text-sm font-medium text-blue-900">Selected File:</p>
              <p className="text-sm text-blue-700">{selectedFile.name}</p>
              <p className="text-xs text-blue-600 mt-1">
                {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
              </p>
            </div>
          )}

          {/* Description Input */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Description (Optional)
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Add a description for this document..."
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
              disabled={uploading}
            />
          </div>

          {/* Upload Progress */}
          {uploading && (
            <div className="space-y-2">
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div
                  className="bg-red-600 h-2 rounded-full transition-all duration-300"
                  style={{ width: `${uploadProgress}%` }}
                ></div>
              </div>
              <p className="text-sm text-gray-600 text-center">
                Uploading... {uploadProgress}%
              </p>
            </div>
          )}

          {/* Upload Button */}
          <Button
            onClick={handleUpload}
            disabled={!selectedFile || uploading}
            className="w-full bg-red-600 text-white hover:bg-red-700 disabled:bg-gray-400"
          >
            {uploading ? 'Uploading...' : 'Upload Document'}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
